import {
  asyncScheduler,
  combineLatest,
  defer,
  EMPTY,
  from,
  interval,
  merge,
  type MonoTypeOperatorFunction,
  Observable,
  type ObservableInput,
  of,
  Subscription,
  type ObservedValueUnionFromArray,
  BehaviorSubject,
  timer,
  Subject,
  mergeWith,
  NEVER,
  type SchedulerLike,
  queueScheduler,
} from 'rxjs'
import {
  distinctUntilChanged,
  endWith,
  filter,
  find,
  map,
  shareReplay,
  startWith,
  switchMap,
  share,
  takeWhile,
  tap,
  take,
} from 'rxjs/operators'
import { isNotNil } from './object-util'
import { match, DEFAULT } from './pattern-match-util'
import { memoize } from '@/utils/memoize-util'
import { onAbort } from '@/utils/abort-util'

/**
 * RxJS filter for filtering out nil values.
 */
export function filterNonNil<T>() {
  return (source$: Observable<T | null | undefined>) =>
    source$.pipe(filter(isNotNil))
}

/**
 * Returns an Observable that emits the initial or latest value on subscribe,
 * and also makes sure only a single subscription is made to the source.
 *
 * This means that **anything that goes before this operator** is triggered only once for
 * concurrent subscriptions.
 *
 * @param initialValue
 */
export function shareBehavior<T>(initialValue: T) {
  return (source$: Observable<T>) =>
    source$.pipe(
      share({
        connector: () => new BehaviorSubject(initialValue),
        resetOnError: false,
        resetOnComplete: false,
        resetOnRefCountZero: false,
      }),
    )
}

/**
 * Shortcut for shareReplay with ref-counting and a buffer size of 1.
 */
export function shareLatest<T>() {
  return (source$: Observable<T>) =>
    source$.pipe(shareReplay({ refCount: true, bufferSize: 1 }))
}

/**
 * Distinct but with a cache size.
 *
 * @param cacheSize
 * @param keySelector
 */
export function distinctCount<T>(
  cacheSize: number,
  keySelector?: (v: T) => string,
) {
  return (source$: Observable<T>): Observable<T> => {
    return new Observable<T>((observer) => {
      return source$.subscribe({
        error: observer.error.bind(observer),
        complete: observer.complete.bind(observer),
        next: memoize(observer.next.bind(observer), {
          maxCount: cacheSize,
          keySelector: keySelector ? (args) => keySelector(args[0]) : undefined,
        }),
      })
    })
  }
}

/**
 * When `signal$` emits `true`, values flow through. When `false`, they queue up, and get flushed
 * when it emits `true` again. If `signal$` completes, flow is enabled.
 *
 * @param signal$
 * @param initial
 */
export function flowToggle<T>(signal$: Observable<boolean>, initial = false) {
  return (source$: Observable<T>) =>
    new Observable<T>((observer) => {
      let queue: T[] = []
      let flowing = initial
      const signalSub = signal$.subscribe({
        next: (shouldFlow) => {
          flowing = shouldFlow
          if (flowing) {
            flush()
          }
        },
        error: observer.error.bind(observer),
        complete: () => {
          flowing = true
          flush()
        },
      })

      const sourceSub = source$.subscribe({
        error: observer.error.bind(observer),
        complete: () => {
          flush()
          observer.complete()
        },
        next: (value) => {
          if (flowing) {
            return observer.next(value)
          }
          queue.push(value)
        },
      })

      return () => {
        signalSub.unsubscribe()
        sourceSub.unsubscribe()
      }

      function flush() {
        queue.forEach((v) => observer.next(v))
        queue = []
      }
    })
}

/**
 * Creates an interval that emits the current time initially and every second.
 */
export function fromClock(
  /* istanbul ignore next */
  getNow = () => new Date(),
): Observable<Date> {
  return interval(1000).pipe(
    startWith(0),
    map(() => getNow()),
  )
}

/**
 * Calls `factory` with an abort signal to get an observable input and subscribes
 * to it, while managing the abort controller. Aborts are not thrown as errors.
 */
export function withAbortSignal<T>(
  factory: (signal: AbortSignal) => ObservableInput<T>,
) {
  return new Observable<T>((subscriber) => {
    let controller = new AbortController()
    const subscription = from(factory(controller.signal)).subscribe({
      complete: subscriber.complete.bind(subscriber),
      error: subscriber.error.bind(subscriber),
      next: (value) => {
        subscriber.next(value)
      },
    })
    return () => {
      controller.abort()
      controller = new AbortController()
      subscription.unsubscribe()
    }
  })
}

/**
 * Takes an observable of pairs and returns an observable for the first element
 * from the pair.
 */
export function unpair<T extends unknown[]>() {
  return (source$: Observable<T>): Observable<T[0]> =>
    source$.pipe(map(([x]) => x))
}

/**
 * Like a combination of `switchMap` and `exhaustMap`, but when a new source value is emitted, ignores values from the
 * inner observable and waits for it to complete, then subscribes to the last queued item.
 *
 * Useful for setting up syncing pipelines (spam save requests, always be saving but only one at a time and only the last one).
 *
 * source:     --1---------2--3|
 * project(1): --(a1)--(b1)------(c1)|
 * project(2):  (never subscribed)
 * project(3):                       (a3)---(b3)---(c3)--|
 * output:     --(a1)--(b1)----------(a3)---(b3)---(d3)--|
 *
 */
export function tailMap<T, R>(
  project: (item: T, index: number) => ObservableInput<R>,
  resultSelector = (item: R): R => item,
) {
  return (source$: Observable<T>): Observable<R> =>
    new Observable<R>((subscriber) => {
      let sourceCompleted = false
      let inProgress = false
      let hasQueued = false
      let queuedValue: T | undefined
      let index = 0
      let innerSub = new Subscription()
      const sub = new Subscription()
      sub.add(
        source$.subscribe({
          next: onSourceNext,
          error: subscriber.error.bind(subscriber),
          complete: onSourceComplete,
        }),
      )
      return sub

      /**
       * Handler for when the source emits a value.
       *
       * @param value
       */
      function onSourceNext(value: T) {
        if (inProgress) {
          hasQueued = true
          queuedValue = value
          return
        }

        next(value)
      }

      /**
       * Starts the next subscription.
       *
       * @param value
       */
      function next(value: T) {
        inProgress = true
        innerSub.unsubscribe()
        sub.remove(innerSub)
        // defer handles errors too
        innerSub = defer(() => project(value, index++)).subscribe({
          next: onInnerNext,
          complete: onInnerComplete,
          error: onInnerError,
        })
        sub.add(innerSub)
      }

      /**
       * When the inner observable emits, forward it if it is the tail.
       *
       * @param value
       */
      function onInnerNext(value: R) {
        // If this isn't the tail, don't emit.
        if (!hasQueued) {
          subscriber.next(resultSelector(value))
        }
      }

      /**
       * When the inner observable errors, forward it if it is the tail.
       *
       * @param error
       */
      function onInnerError(error: unknown) {
        // If this the tail, forward the error.
        if (!hasQueued) {
          subscriber.error(error)
        } else {
          // If it's not, ignore the error and process the tail item.
          clearAndEmitTail()
        }
      }

      /**
       * When the source completes, if we haven't mapped anything yet,
       * complete the stream.
       */
      function onSourceComplete() {
        if (!inProgress) {
          subscriber.complete()
        }
        sourceCompleted = true
      }

      /**
       * When the inner observable completes, if the source has completed,
       * complete the observable.
       */
      function onInnerComplete() {
        inProgress = false
        if (hasQueued) {
          clearAndEmitTail()
          return
        }

        if (sourceCompleted) {
          subscriber.complete()
        }
      }

      /**
       * Emits the tail value and clears it.
       */
      function clearAndEmitTail() {
        hasQueued = false
        const value = queuedValue!
        queuedValue = undefined
        next(value)
      }
    })
}

/**
 * Subscribes to the source when `isActive$` emits `true`, unsubscribes
 * when `false`. When inactive, the returned `Observable` will never complete.
 *
 * @param isActive$ The observable that controls whether we are
 *  subscribing or not.
 */
export function whileActive<T>(
  isActive$: Observable<boolean>,
): MonoTypeOperatorFunction<T> {
  return (source$) =>
    isActive$.pipe(
      distinctUntilChanged(),
      switchMap((isActive) => (isActive ? source$ : EMPTY)),
    )
}

/**
 * Mirrors the source but also emits the latest item from source when the
 * signal emits.
 *
 * @param signal$
 */
export function emitLatestWhen<T>(
  signal$: Observable<unknown>,
): MonoTypeOperatorFunction<T> {
  return (source$) =>
    source$.pipe(
      switchMap((item) => merge(of(item), signal$.pipe(map(() => item)))),
    )
}

/**
 * Returns an {@link AbortSignal} that aborts either when the observable
 * emits a value that passes the specified predicate, or when it completes.
 *
 * If a predicate is not provided, it aborts when the first item emits.
 *
 * @param source$
 * @param predicate
 */
export function cancelWhen<T>(
  source$: Observable<T>,
  predicate?: (item: T) => boolean,
) {
  const controller = new AbortController()
  const abort = () => controller.abort()
  const sub = source$.pipe(find(predicate ?? (() => true))).subscribe({
    error: abort,
    complete: abort,
  })

  const dispose = () => sub.unsubscribe()
  controller.signal.addEventListener('abort', dispose)

  return {
    controller,
    dispose,
    signal: controller.signal,
    [Symbol.dispose]: dispose,
  }
}

/**
 * Creates an observable that emits the abort error and completes when
 * the abort signal aborts. Does not emit or complete otherwise.
 */
export function fromAbortSignal(signal: AbortSignal | null | undefined) {
  if (!signal) {
    return NEVER
  }

  return new Observable((observer) =>
    onAbort(signal, () => {
      observer.next(signal?.reason)
      observer.complete()
    }),
  )
}

/**
 * Emits an error when the signal aborts.
 *
 * @param signal
 */
export function throwOnAbort<T>(
  signal: AbortSignal | null | undefined,
): MonoTypeOperatorFunction<T> {
  return (source$) => {
    return new Observable<T>((observer) => {
      const cleanup = onAbort(signal, () => {
        observer.error(signal?.reason)
      })
      source$.subscribe({
        next: observer.next.bind(observer),
        error: (err) => {
          cleanup()
          observer.error(err)
        },
        complete: () => {
          cleanup()
          observer.complete()
        },
      })
    })
  }
}

/**
 * Aborts the signal when the observable emits a value.
 *
 * @param observable
 * @param reason
 */
export function toAbortSignal(
  observable: Observable<unknown>,
  reason?: unknown,
): {
  signal: AbortSignal
  [Symbol.dispose]: () => void
} {
  const controller = new AbortController()
  const subscription = observable.pipe(take(1)).subscribe(() => {
    controller.abort(reason)
  })
  return {
    signal: controller.signal,
    [Symbol.dispose]: () => subscription.unsubscribe(),
  }
}

/**
 * Returns a Cancellation Token that cancels either when the input observable
 * emits `false` or when it completes.
 *
 * @param active$
 */
export function cancelOnDeactivate(active$: Observable<boolean>) {
  return cancelWhen(active$, (v) => !v)
}

/**
 * Batches elements based on time or max elements, whichever comes first.
 *
 * @param waitTimeSpan The amount of time to wait for elements before we emit the batch
 * @param maxElementsPerBatch If the batch size reaches this number, emit it without waiting. Defaults to infinity.
 * @param scheduler The scheduler to use.
 */
export function batchElements<T>(
  waitTimeSpan: number,
  maxElementsPerBatch = Number.POSITIVE_INFINITY,
  scheduler = asyncScheduler,
) {
  return (source$: Observable<T>): Observable<T[]> =>
    new Observable((subscriber) => {
      let currentBatch: T[] = []
      let scheduledFlush: Subscription | undefined
      let completed = false
      const subscription = source$.subscribe({
        error(err) {
          subscriber.error(err)
          clearSchedule()
        },
        next(value) {
          currentBatch.push(value)
          if (currentBatch.length >= maxElementsPerBatch) {
            maybeFlushBatch()
            return
          }

          scheduledFlush ??= scheduler.schedule(maybeFlushBatch, waitTimeSpan)
        },
        complete() {
          completed = true
          maybeFlushBatch()
        },
      })

      /**
       * Flushes the batch if there is one.
       * If we received a completion signal from the source,
       * complete the observable afterwards.
       */
      function maybeFlushBatch() {
        const batch = currentBatch
        currentBatch = []
        clearSchedule()
        if (batch.length > 0) {
          subscriber.next(batch)
        }

        if (completed) {
          subscriber.complete()
        }
      }

      /**
       * Clears any scheduled item.
       */
      function clearSchedule() {
        scheduledFlush?.unsubscribe()
        scheduledFlush = undefined
      }

      return () => {
        subscription.unsubscribe()
      }
    })
}

/**
 * Options for {@link slidingBufferTime}.
 */
interface SlidingBufferTimeOptions {
  /**
   * The time in milliseconds after which the buffer will be released if
   * no element has been seen within that time. The slide time resets
   * when a new element is emitted on the source.
   */
  slide: number
  /**
   * The max time in milliseconds after which the buffer will be released.
   * When used, should be higher than the slide time.
   *
   * Defaults to the value of `slide`.
   */
  maxWait?: number
  /**
   * Max amount of elements per batch.
   *
   * Defaults to 0, which means no limit.
   */
  count?: number
  /**
   * Optional scheduler to use for scheduling the buffer release.
   *
   * Defaults to `asyncScheduler`.
   */
  scheduler?: SchedulerLike
}

/**
 * Represented as `Observable.BufferTime.cs` in Payouts API, the repository from which it was ported.
 *
 * Buffers elements until either a sliding time window expires, a max wait timer expires, or a max count of elements are buffered.
 *
 * The sliding time window acts like a buffer-level `debounce` - it'll release the buffer is `slide` time goes past since the last emission.
 * The max wait count starts counting from the first emission - it'll release the buffer if the buffer has been filling for longer than that time.
 * The count will release the buffer if the buffer exceeds the count.
 */
export function slidingBufferTime<T>({
  slide,
  maxWait = slide,
  count = 0,
  scheduler = asyncScheduler,
}: SlidingBufferTimeOptions) {
  let batch: T[] = []
  let slideSubscription: Subscription
  let maxWaitSubscription: Subscription

  const isInfinite = count === 0
  const useSlidingTimer = slide !== maxWait

  return (source: Observable<T>) => {
    return new Observable<T[]>((output) => {
      source.subscribe({
        next: onNext,
        complete: onComplete,
        error: onError,
      })

      function onNext(value: T) {
        const isFirst = batch.length === 0
        batch.push(value)

        if (!isInfinite && batch.length >= count) {
          slideSubscription?.unsubscribe()
          maxWaitSubscription?.unsubscribe()
          closeBatch()
          return
        }

        if (useSlidingTimer) {
          slideSubscription?.unsubscribe()
          slideSubscription = scheduler.schedule(() => {
            slideSubscription?.unsubscribe()
            maxWaitSubscription?.unsubscribe()
            closeBatch()
          }, slide)
        }

        if (isFirst) {
          maxWaitSubscription?.unsubscribe()
          maxWaitSubscription = scheduler.schedule(() => {
            slideSubscription?.unsubscribe()
            maxWaitSubscription?.unsubscribe()
            closeBatch()
          }, maxWait)
        }
      }

      function onError(error: Error) {
        output.error(error)
      }

      function onComplete() {
        closeBatch()
        output.complete()
      }

      function closeBatch() {
        if (batch.length === 0) {
          return
        }

        const copy = batch
        batch = []
        output.next(copy)
      }
    })
  }
}

/**
 * Creation operator that turns the input array of observables
 * into an observable that emits `true` while all conditions are met
 * and `false` when they don't.
 *
 * Uses `combineLatest` and `distinctUntilChanged`.
 *
 * @param inputs
 * @param predicate
 */
export function every<T extends ObservableInput<unknown>[]>(
  inputs: T,
  predicate: (value: ObservedValueUnionFromArray<T>) => boolean = Boolean,
): Observable<boolean> {
  return combineLatest(inputs).pipe(
    map((conditions) =>
      (conditions as ObservedValueUnionFromArray<T>[]).every(predicate),
    ),
    distinctUntilChanged(),
  )
}

/**
 * Creation operator that turns the input array of observables
 * into an observable that emits `true` while some conditions are met
 * and `false` when none do.
 *
 * Uses `combineLatest` and `distinctUntilChanged`.
 *
 * @param inputs
 * @param predicate
 */
export function some<T extends ObservableInput<unknown>[]>(
  inputs: T,
  predicate: (value: ObservedValueUnionFromArray<T>) => boolean = Boolean,
): Observable<boolean> {
  return combineLatest(inputs).pipe(
    map((conditions) =>
      (conditions as ObservedValueUnionFromArray<T>[]).some(predicate),
    ),
    distinctUntilChanged(),
  )
}

/**
 * Executes `callback` when unsubscribing.
 *
 * @param callback
 */
export function teardown<T>(callback: () => void) {
  return (source: Observable<T>) =>
    new Observable<T>((observer) => {
      const subscription = source.subscribe(observer)
      return () => {
        callback()
        subscription.unsubscribe()
      }
    })
}

/**
 * Emits an observable countdown of seconds remaining until the target date.
 */
export function countdown(target: Date): Observable<number> {
  const difference = differenceBetweenNowAndTargetInSeconds(target)

  return timer(delayToRoundSecond(new Date()), 1000).pipe(
    map((x: number) => difference - x),
    takeWhile((x) => x > 0),
    startWith(difference),
    endWith(0),
  )

  function delayToRoundSecond(date: Date) {
    return 1000 - date.getMilliseconds()
  }

  function differenceBetweenNowAndTargetInSeconds(date: Date) {
    const millisecondDifference = Math.round(
      date.getTime() - new Date().getTime(),
    )

    return millisecondDifference / 1000
  }
}

/**
 * Indicates the interval upon which the clock should tick.
 */
export type TickEvery = 'second' | 'minute' | 'hour'

/**
 * Collects the first emission into a batch and calls selector with it as input.
 * Subsequent emissions will be buffered into batches of the specified size or until
 * the observable returned by selector completes.
 * It will keep creating buffers until the source completes, so you should be applying
 * backpressure yourself.
 *
 * A common use case is when processing values in real-time and batching is preferred
 * when there is a backlog of values to process. That means that if the processing
 * function is as fast or faster than the rate of input values, then the selector should
 * only be called with batches of one item each. However, if the rate of values from the
 * source is higher than what we can process, then they will be batched.
 *
 * @param size The max amount of elements to include in a single batch
 * @param selector Called with a batch, returns another observable that will be subscribed to until it completes
 * @param scheduler Optional scheduler
 * @returns An observable that emits values from the selector
 */
export function bufferMap<T, R>(
  size: number,
  selector: (batch: readonly T[]) => ObservableInput<R>,
  scheduler: SchedulerLike = queueScheduler,
) {
  return (source$: Observable<T>): Observable<R> =>
    new Observable<R>((observer) => {
      let buffering = false
      let sourceCompleted = false
      const buffers: T[][] = []
      let innerSub = new Subscription()
      const sub = new Subscription()

      sub.add(
        source$.subscribe({
          next: (value) => {
            // Ensures recursive observables don't cause stack overflows.
            scheduler.schedule(() => {
              // If not buffering, call the selector immediately with a buffer of just this item.
              // While we wait for that to complete, we will buffer future values.
              if (!buffering) {
                buffering = true
                subscribeToInner([value])
                return
              }

              // Otherwise, check if we have a buffer.
              let buffer = buffers[buffers.length - 1]
              if (!buffer) {
                // No buffer, create one!
                buffer = [value]
                buffers.push(buffer)
                return
              }

              // We had a buffer, check if it's at the size limit.
              if (buffer.length < size) {
                // There's room; add it to this buffer.
                buffer.push(value)
                return
              }

              // There was no room in the last buffer, create another one and add it to our
              // queue of buffers.
              buffer = [value]
              buffers.push(buffer)
            })
          },
          error: (error) => {
            // When we receive an error, bubble it up to the observer.
            // Cancel any ongoing inner processing
            innerSub.unsubscribe()
            observer.error(error)
          },
          complete: () => {
            sourceCompleted = true
            if (!buffering) {
              // If we are not buffering, then there are no messages being processed,
              // so the completion of the source should complete the resulting sequence.
              observer.complete()
            }
          },
        }),
      )

      sub.add(innerSub)
      return sub

      /**
       * Subscribes to the inner observable by calling the selector with the given batch.
       */
      function subscribeToInner(batch: readonly T[]) {
        innerSub.unsubscribe()
        sub.remove(innerSub)

        try {
          innerSub = from(selector(batch)).subscribe({
            next: (value) => observer.next(value),
            error: (error) => observer.error(error),
            complete: () => {
              // When the inner observable completes, flush the buffer.
              // If the outer observable has finished and there are no more buffers,
              // complete the observer.
              innerSub.unsubscribe()
              innerSub = new Subscription()
              sub.add(innerSub)

              const nextBuffer = buffers.shift()
              if (nextBuffer) {
                subscribeToInner(nextBuffer)
              } else {
                buffering = false
                if (sourceCompleted) {
                  observer.complete()
                }
              }
            },
          })
          sub.add(innerSub)
        } catch (error) {
          observer.error(error)
        }
      }
    })
}

/**
 * A clock observable. Ticks at the specified interval. If you specify
 * a well-defined `TickEvery` enum, ticks are synchronized with clock time.
 *
 * If you provide an arbitrary millisecond interval, ticks are aligned
 * only to the nearest whole second.
 */
export function clock(tickEvery: TickEvery | number) {
  const intervalDuration = !isTickEveryEnum(tickEvery)
    ? tickEvery
    : match(tickEvery, {
        second: () => 1000,
        minute: () => 60_000,
        hour: () => 60_000_000,
      })

  return timer(delayToRoundStartTime(new Date()), intervalDuration).pipe(
    map(() => new Date()),
    startWith(new Date()),
  )

  /**
   * Computes the delay to ensure a round start time in sync with the real time.
   */
  function delayToRoundStartTime(date: Date) {
    return match(tickEvery, {
      second: () => 1000 - date.getMilliseconds(),
      minute: () => (60 - date.getSeconds()) * 1000,
      hour: () => (60 - date.getMinutes()) * 60 * 1000,

      // Handles the arbitrary intervals.
      [DEFAULT]: () => 1000 - date.getMilliseconds(),
    })
  }

  function isTickEveryEnum(value: unknown): value is TickEvery {
    return value === 'second' || value === 'minute' || value === 'hour'
  }
}

/**
 * Like `expand` but unsubscribes from the inner observable
 * when it emits a value.
 *
 * Consider this similar to `switchMap` vs `mergeMap`.
 */
export function switchExpand<T>(project: (item: T) => ObservableInput<T>) {
  return (source$: Observable<T>) => {
    // Used to feed the value that was emitted from the `project` function
    // back into the beginning of the pipeline, triggering `switchMap`
    // to unsubscribe and call `project` again with the produced value.
    const input = new Subject<T>()
    return source$.pipe(
      tap({ complete: () => input.complete() }),
      mergeWith(input),
      switchMap((item) =>
        from(project(item)).pipe(
          tap({ next: (inner) => input.next(inner) }),
          startWith(item),
        ),
      ),
    )
  }
}
